import { Dropdown } from '@utima/ui';
import { Check, ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  /**
   * The selected values (array of strings)
   */
  value: string[];

  /**
   * If empty selection is not allowed, you can specify a forced default value.
   */
  forcedDefaultValue?: string;

  /**
   * Called whenever the selection changes
   */
  onChange: (newValues: string[]) => void;

  /**
   * The list of options to render
   */
  options: MultiSelectOption[];

  /**
   * Placeholder text shown when no items are selected
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * A short label to display when items are selected
   */
  label?: string;

  /**
   * Optional fixed max height for the dropdown
   */
  maxDropdownHeight?: string;
}

/**
 * A generic multi-select using
 * TODO - make it part of the UI library? (add multi-select option to existing Select component)
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  label,
  forcedDefaultValue,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggleItem = (itemValue: string) => {
    if (value.includes(itemValue)) {
      // Removing itemValue
      const newValues = value.filter(v => v !== itemValue);
      if (newValues.length === 0 && forcedDefaultValue) {
        // If forcedDefaultValue is set, re-add it if the new array is empty
        // Only do so if forcedDefaultValue is a valid option
        if (options.some(o => o.value === forcedDefaultValue)) {
          onChange([forcedDefaultValue]);
        } else {
          // If forcedDefaultValue is not among the options, fallback to ignoring
          onChange(value); // revert
        }
      } else {
        onChange(newValues);
      }
    } else {
      // Adding itemValue
      // If we had only forcedDefaultValue, removing it is okay if there's another item
      onChange([...value, itemValue]);
    }
  };

  // Display label logic:
  // - 0 selected => placeholder
  // - 1 selected => that item’s label
  // - multiple => "<firstLabel> + X others"
  let triggerLabel = placeholder;
  if (value.length === 1) {
    const firstLabel =
      options.find(opt => opt.value === value[0])?.label || placeholder;
    triggerLabel = firstLabel;
  } else if (value.length > 1) {
    const firstLabel = options.find(opt => opt.value === value[0])?.label || '';
    triggerLabel = `${firstLabel} + ${value.length - 1} další`;
  }

  return (
    <>
      {label && (
        <label
          className='mb-1 block cursor-pointer text-sm text-gray-500'
          onClick={() => {
            if (triggerRef.current) {
              triggerRef.current.focus();
              setOpen(true);
            }
          }}
        >
          {label}
        </label>
      )}
      <Dropdown.Root open={open} onOpenChange={setOpen}>
        <Dropdown.Trigger asChild>
          <button
            data-cta='admin-multiselect-trigger'
            type='button'
            disabled={disabled}
            className={[
              'outline-none focus:ring-2 focus:ring-offset-2 ring-input-border peer transition-all text-input-fg font-normal flex justify-between items-center',
              'w-full rounded-radius border placeholder-placeholder bg-input disabled:cursor-not-allowed disabled:opacity-60 h-9 px-3 py-2 text-sm border-input-border focus:border-input-fg hover:border-input-fg',
            ].join(' ')}
          >
            <span className='min-w-0 truncate'>{triggerLabel}</span>
            <ChevronDown className='ml-2 size-4 shrink-0' />
          </button>
        </Dropdown.Trigger>

        {!disabled && (
          <Dropdown.Content className='max-h-60 min-w-48 overflow-y-auto'>
            {options.map(opt => {
              const isChecked = value.includes(opt.value);

              return (
                <Dropdown.Item
                  key={opt.value}
                  onSelect={event => {
                    event.preventDefault();
                    toggleItem(opt.value);
                  }}
                  className='flex cursor-pointer items-center gap-2'
                >
                  <span className='flex size-4 shrink-0 items-center justify-center'>
                    {isChecked ? <Check className='size-4' /> : null}
                  </span>
                  <span>{opt.label}</span>
                </Dropdown.Item>
              );
            })}
          </Dropdown.Content>
        )}
      </Dropdown.Root>
    </>
  );
}
